import { Icon } from "@iconify/react";
import menu2Fill from "@iconify-icons/mdi/menu";
import { useLocation, useNavigate } from "react-router-dom";
import { AppBar, Box, ButtonBase, IconButton, Toolbar, Tooltip, Typography, useTheme } from "@mui/material";

import { MHidden } from "../../components/@material-extend";
import { GlobalSearch, UserAvatar } from "../../components/ui";
import { useAuth } from "../../contexts/auth2";
import { getRouteHeading } from "./route-headings";
import { SIDEBAR_WIDTH } from "./DashboardSidebar";

// ----------------------------------------------------------------------

export const TOPBAR_MOBILE = 60;
export const TOPBAR_DESKTOP = 84;

interface DashboardNavbarProps {
  onOpenSidebar: () => void;
}

export default function DashboardNavbar({ onOpenSidebar }: DashboardNavbarProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const heading = getRouteHeading(pathname);
  const isAdm = pathname.startsWith("/adm");

  // Admin area on desktop: render nothing (each admin page owns its own title)
  if (isAdm) {
    return (
      <>
        {/* Mobile/tablet only: slim bar with just the hamburger to open the sidebar drawer */}
        <MHidden width="lgDown">
          {/* Desktop: no topbar in admin */}
          <span style={{ display: "none" }} />
        </MHidden>
        <MHidden width="lgUp">
          <AppBar
            elevation={0}
            sx={{
              backdropFilter: "blur(8px)",
              backgroundColor: theme.palette.brand.topbar,
              borderBottom: `1px solid ${theme.palette.divider}`,
              boxShadow: "none",
              color: "text.primary",
            }}
          >
            <Toolbar sx={{ minHeight: TOPBAR_MOBILE, px: 2.25 }}>
              <Tooltip title="Abrir menu" placement="top">
                <IconButton onClick={onOpenSidebar} aria-label="Abrir menu" sx={{ color: "text.primary" }}>
                  <Icon icon={menu2Fill} />
                </IconButton>
              </Tooltip>
            </Toolbar>
          </AppBar>
        </MHidden>
      </>
    );
  }

  return (
    <AppBar
      elevation={0}
      sx={{
        backdropFilter: "blur(8px)",
        backgroundColor: theme.palette.brand.topbar,
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: "none",
        color: "text.primary",
        [theme.breakpoints.up("lg")]: {
          width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
          left: SIDEBAR_WIDTH,
        },
      }}
    >
      <Toolbar sx={{ minHeight: { xs: TOPBAR_MOBILE, lg: TOPBAR_DESKTOP }, px: { xs: 2.25, lg: 4 }, gap: 2.5 }}>
        <MHidden width="lgUp">
          <Tooltip title="Abrir menu" placement="top">
            <IconButton onClick={onOpenSidebar} aria-label="Abrir menu" sx={{ color: "text.primary" }}>
              <Icon icon={menu2Fill} />
            </IconButton>
          </Tooltip>
        </MHidden>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h1"
            noWrap
            sx={{
              fontFamily: theme.typography.fontFamilyDisplay,
              fontSize: { xs: 22, lg: 27 },
              lineHeight: 1.1,
              color: "text.primary",
            }}
          >
            {heading.title}
          </Typography>
          {heading.subtitle && (
            <MHidden width="lgDown">
              <Typography noWrap sx={{ mt: "2px", fontSize: "12.5px", color: "text.secondary" }}>
                {heading.subtitle}
              </Typography>
            </MHidden>
          )}
        </Box>

        <MHidden width="lgDown">
          <GlobalSearch />
        </MHidden>

        <MHidden width="lgUp">
          <Tooltip title="Meu perfil" placement="top">
            <ButtonBase
              onClick={() => navigate("/dashboard/profile")}
              aria-label="Meu perfil"
              sx={{ borderRadius: "50%" }}
            >
              <UserAvatar label={user.email ?? "?"} />
            </ButtonBase>
          </Tooltip>
        </MHidden>
      </Toolbar>
    </AppBar>
  );
}
